
import { EtsyOrder } from "@/types/etsy";

export const printOrdersBulk = (orders: EtsyOrder[]) => {
  if (orders.length === 0) {
    alert('No orders to print');
    return;
  }

  // Helper function to get all product names
  const getProductNames = (order: EtsyOrder): string => {
    if (order.items && order.items.length > 0) {
      return order.items.map(item => item.title).join('; ');
    }
    return order.productName;
  };

  // Helper function to get location string
  const getLocationString = (order: EtsyOrder): string => {
    const parts = [];
    if (order.city) parts.push(order.city);
    if (order.country) parts.push(order.country);
    if (order.zip) parts.push(order.zip);
    return parts.join(', ');
  };

  // Create a styled version of orders for printing
  const printableContent = `
    <html>
      <head>
        <title>Orders Print</title>
        <style>
          @media print {
            body { 
              font-family: 'Segoe UI', Arial, sans-serif; 
              font-size: 13px;
              background: #f5f6fa;
              margin: 0;
              padding: 0;
            }
            .print-brand {
              text-align: center;
              font-size: 2rem;
              letter-spacing: 2px;
              font-weight: bold;
              margin-top: 24px;
              margin-bottom: 8px;
              color: #343a40;
            }
            .print-header {
              text-align: center;
              font-size: 1.2rem;
              margin-bottom: 18px;
              font-weight: 600;
              color: #4f5d75;
            }
            .order-list {
              width: 100%;
              max-width: 760px;
              margin: 0 auto;
            }
            .order-card {
              background: #fff;
              border: 1px solid #e6e6e6;
              border-radius: 10px;
              margin-bottom: 24px;
              box-shadow: 0 2px 8px rgba(80,80,120,0.05);
              break-inside: avoid;
              page-break-inside: avoid;
              padding: 20px 30px;
              transition: box-shadow 0.2s;
            }
            .order-card:last-child {
              margin-bottom: 0;
            }
            .order-title {
              font-size: 1.1rem;
              margin-bottom: 8px;
              color: #22223b;
              font-weight: 700;
              display: flex;
              justify-content: space-between;
              border-bottom: 1px solid #ececec;
              padding-bottom: 5px;
            }
            .order-details {
              margin-top: 10px;
              margin-bottom: 8px;
              color: #596172;
              font-size: 1em;
              line-height: 1.45;
            }
            .order-details label {
              font-weight: 600;
              color: #43485c;
              margin-right: 4px;
            }
            .order-footer {
              margin-top: 14px;
              font-size: 0.98em;
              color: #bbb;
              text-align: right;
            }
            .section {
              margin-top: 8px;
              margin-bottom: 7px;
            }
            .section-title {
              font-size: 1em;
              color: #343a40;
              font-weight: 600;
              margin-bottom: 1px;
              margin-top: 10px;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-brand">
          Etsy Orders
        </div>
        <div class="print-header">
          Order Summary (${orders.length} orders)
        </div>
        <div class="order-list">
          ${orders.map(order => `
            <div class="order-card">
              <div class="order-title">
                <span>Order #${order.receiptId}</span>
                <span style="font-size:0.98em;font-weight:500;color:#1b6d53">${order.status}</span>
              </div>
              <div class="order-details">
                <div class="section">
                  <span class="section-title">Customer</span><br/>
                  <label>Name:</label>${order.customerName}<br/>
                  ${order.customerEmail ? `<label>Email:</label>${order.customerEmail}<br/>` : ""}
                </div>
                <div class="section">
                  <span class="section-title">Product</span><br/>
                  <label>Product:</label>${getProductNames(order)}<br/>
                </div>
                <div class="section">
                  <span class="section-title">Shipping Address</span><br/>
                  ${order.address && order.address !== 'N/A' ? order.address + '<br/>' : ""}
                  ${getLocationString(order)}
                </div>
                <div class="section">
                  <span class="section-title">Payment</span><br/>
                  <label>Net Amount:</label>${order.subtotalAmount && order.currencyCode ? `${order.subtotalAmount} ${order.currencyCode}` : 'N/A'}<br/>
                  <label>Total Amount:</label>${order.totalAmount && order.currencyCode ? `${order.totalAmount} ${order.currencyCode}` : 'N/A'}
                </div>
                <div class="section">
                  <span class="section-title">Order Details</span><br/>
                  <label>Date:</label>${order.date}<br/>
                  <label>Tracking:</label>${order.trackingId && order.trackingId !== 'N/A' ? order.trackingId : 'No tracking'}
                </div>
              </div>
              <div class="order-footer">
                &copy; ${new Date().getFullYear()} Etsy Bulk Print
              </div>
            </div>
          `).join('')}
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(printableContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  } else {
    alert('Please allow pop-ups to print orders');
  }
};
